import React, { useCallback, useContext, useEffect } from 'react'
import { useState } from 'react';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import {
    Autocomplete,
    Box,
    Tab,
    Tabs,
    Button,
    FormControl,
    Stack,
    TextField,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import LabeledCheckbox from '../../TemplateProperties/TPCheckbox';
import { stringifyJSONAttributes, stringToBooleanConversion } from '../../../utils/utilFunctions';
import { ParentScreenCtx } from '../../../pages/workbench_build/WorkbenchBuild';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../redux/api';
import Repository from '../../Repository/Repository';
import { cloneDeep, debounce, isArray } from 'lodash';
import { treeActions } from '../../../redux/slices/treeSlice';
import { eachDeep } from "deepdash-es/standalone";
import ColumnReturnValues from './ColumnReturnValues';
import DynamicClauseValues from './DynamicClauseValues';
import TabPanel from '../../TabPanel';
import SQLQueryPreview from './SQLQueryPreview';
import DatabaseNavigator from './DataTypeComponents/DatabaseNavigator';
import { encodingConstant } from '../../../constants/constantList';
import SchemaModal from './DataTypeComponents/SchemaModal';
import LightTooltip from '../../../commonComponents/LightTooltip';

const CustomDBProperties = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const parentScreen = useContext(ParentScreenCtx);
    const { account, user } = useSelector((state) => state.queryParams);
    const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
    const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
    const listUpdated = useSelector((state) => state.treeData.pdcListUpdated);
    const [dbName, setDbName] = useState('');
    const [connectionList, setConnectionList] = useState([]);
    const [connection, setConnection] = useState('');
    const [pdcModal, setPdcModal] = useState(null);
    const [cacheResult, setCacheResult] = useState(false);
    const [sqlQuery, setSqlQuery] = useState('');
    const [currTab, setCurrTab] = useState(0);
    const [inArgs, setInArgs] = useState([]);
    const [outArgs, setOutArgs] = useState([]);
    const [queryAccount, setQueryAccount] = useState('');
    const [sqlQueryPreview, setSqlQueryPreview] = useState(false);
    const [schema, setSchema] = useState('');
    const [openDrawer, setOpenDrawer] = useState(false);
    const [connectionLoading, setConnectionLoading] = useState(false);
    const [showAlert, setShowAlert] = useState({ mssg: '', type: 'success', isOpen: false });
    const [schemaModal, setSchemaModal] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);

    const handleClose = () => {
        setOpenDrawer(false);
    }

    const handleAlertClose = () => {
        setShowAlert({ ...showAlert, isOpen: false });
    }

    const handleLoading = (value) => {
        if (value) setConnectionLoading(true);
        else setTimeout(() => setConnectionLoading(false), 500)
    }

    const handleTabChange = (event, newValue) => {
        setCurrTab(newValue);
    };

    const submitChange = (key, val) => {
        if (key !== "PREDEFINED" && key !== 'IS_CACHEABLE' && key !== 'NAME' && key !== 'VALUE' && key !== 'SCHEMA' && key !== 'CONTENT') return;
        const updatedData = eachDeep(cloneDeep(translatedData), (objValue) => {
            if (objValue?.key !== selectedNode.key) {
                return;
            }
            if (key === 'NAME') {
                objValue[key] = val;
                objValue['title'] = `Custom DB Field: ${val}`;
                objValue['MAPNAME'] = val;
            } else if (key === 'IS_CACHEABLE') objValue[key] = val;
            else if (key === 'VALUE' || key === 'PREDEFINED' || key === 'ACCOUNT' || key === 'CONTENT') {
                if (objValue?.QUERY !== null || objValue?.QUERY !== undefined) {
                    const query = JSON.parse(JSON.stringify(objValue?.QUERY));
                    if (key === 'VALUE') query[key] = val;
                    else if (key === 'CONTENT') {
                        query['VALUE'] = val.query;
                        objValue['OUT_ARG'] = val.outArg;
                    }
                    else if (key === 'SCHEMA') query[key] = val;
                    else {
                        const connection = JSON.parse(JSON.stringify(objValue?.QUERY?.CONNECTION));
                        if (key === 'PREDEFINED') connection[key] = val;
                        query['CONNECTION'] = connection;
                    }
                    objValue['QUERY'] = query;
                }
            }
            else objValue[key] = val || selectedNode[key];
            dispatch(
                treeActions.setRightTreeSelectedNode({
                    screen: parentScreen,
                    type: rightTree.currentTab,
                    data: objValue,
                })
            );
        });
        dispatch(
            treeActions.setNewRightTreeTranslatedData({
                screen: parentScreen,
                type: rightTree.currentTab,
                data: updatedData,
            })
        );
    };

    const debounceChangeHandler = useCallback(debounce(submitChange, 200), [
        rightTree.currentTab,
        rightTree.tree,
    ]);

    const updateColumns = (cols = []) => {
        return cols?.map(col => {
            return {
                ARGNAME: col,
                ARGTYPE: 'OUT',
                FIELD_NAME: col,
                ICON_DISPLAY: 'OUT_ARG',
                MAPNAME: col,
                ENCODING: Object.keys(encodingConstant)[0],
            }
        });
    }

    const getColumns = (query) => {
        const match = query.match(/SELECT\s+([\s\S]+?)\s+FROM/i);
        if (match && match[1]) {
            const columns = match[1].split(',').map(col => col.trim());
            return columns;
        }
        return [];
    }

    const handleSqlQueryToRun = (data) => {
        if (typeof data === 'object') {
            const query = data?.res;
            const newColumns = updateColumns(data?.cols);
            setSqlQuery(query);
            setOutArgs(newColumns);
            debounceChangeHandler('CONTENT', {
                query: query,
                outArg: newColumns
            })
        } else {
            setSqlQuery(data);
            const cols = getColumns(data);
            const newColumns = updateColumns(cols);
            setOutArgs(newColumns);
            debounceChangeHandler('CONTENT', { query: data, outArg: newColumns });
        }
    }

    const updateSchema = (val) => {
        setSchema(val);
    }

    const updateConnection = (val) => {
        if (val !== '')
            setConnection(val);
    }

    const handleQueryData = (query) => {
        const { VALUE, CONNECTION: { ACCOUNT, PREDEFINED } } = query;
        setQueryAccount(ACCOUNT || account);
        setConnection((PREDEFINED !== null || PREDEFINED !== '') ? PREDEFINED : t('SELECT'));
        setSqlQuery(VALUE);
    }

    const tablesHandler = (schema, pdc) => {
        const tables = Object.keys(pdc?.[connection]?.[schema] || {});
        if (!tables?.length && connection && schema) {
            dispatch(
                api.endpoints.get_database_table_info.initiate(
                    {
                        account,
                        user,
                        direction: "OUT",
                        pdc_name: pdc,
                        schema_name: schema,
                    },
                    { forceRefetch: true }
                )
            ).then(res => {
                if (res?.data?.current_state === "SUCCESS" && isArray(res?.data?.data?.database_table_list)) {
                } else {
                    const message = res?.data?.message;
                    setShowAlert({ mssg: message, isOpen: true, type: 'warning' })
                }
            })
        }
    }

    const handleSchema = (pdc) => {
        dispatch(
            api.endpoints.getSchemaList.initiate(
                {
                    account,
                    direction: "out",
                    pdc_name: pdc,
                },
                { forceRefetch: true, subscribe: true }
            )
        ).then((res) => {
            if (res.data.current_state === 'SUCCESS') {
                if (res?.data?.data?.length === 0) {
                    setShowAlert({ mssg: 'No Schema Found', isOpen: true, type: 'warning' })
                } else if (res?.data?.data?.length === 1) {
                    setSchema(res.data.data[0] || '')
                    tablesHandler(res?.data?.data?.[0], pdc);
                    setSqlQueryPreview(true);
                    dispatch(treeActions.setConnectedPDC({
                        data: {
                            CONNECTION: pdc,
                            SCHEMA: res.data.data[0]
                        }
                    }));
                } else {
                    setSchemaModal(true);
                }
            }
            setBtnLoading(false);
        })
    }

    const connectToDb = (pdc) => {
        setBtnLoading(true);
        dispatch(
            api.endpoints.connect_to_selected_db.initiate({
                account: account,
                user: user,
                direction: "OUT",
                pdc_name: pdc
            }, { forceRefetch: true }),
        ).then((res) => {
            if (res?.data?.current_state !== 'SUCCESS' && res?.data?.message === 'Failed') {
                setShowAlert({ mssg: res.data.data || res.data.message, isOpen: true, type: 'warning' });
                setBtnLoading(false);
            }
            else handleSchema(pdc); 
        })
    }

    const getConnections = () => {
        dispatch(api.endpoints.getAllPdcDetail.initiate({
            account: account,
            direction: 'OUT'
        })).then(res => {
            if (res?.data?.current_state === 'ERROR' || !res?.data?.predefined_connections) {
                console.log(res?.data?.current_state);
            } else {
                const list = [];
                for (const [key, value] of Object.entries(res?.data?.predefined_connections)) {
                    if (Object.values(value)[0]?.DATABASE_TYPE !== undefined)
                        list.push(Object.keys(value)[0]);
                }
                setConnectionList(list);
            }
        })
    }

    useEffect(() => {
        getConnections();
        setDbName(selectedNode?.MAPNAME || selectedNode?.title?.split(':')[1].trim());
        setCacheResult(stringToBooleanConversion(selectedNode?.IS_CACHEABLE) || false);
        handleQueryData(selectedNode?.QUERY);
        setInArgs(selectedNode?.IN_ARG);
        setOutArgs(selectedNode?.OUT_ARG);
    }, [selectedNode.key]);

    useEffect(() => {
        if (listUpdated) getConnections();
        return () => { dispatch(treeActions.setPdcListUpdated({ status: false })); };
    }, [listUpdated, dispatch]);

    return (
        <Box sx={{ padding: "15px 15px" }}>
            <Stack spacing={"15px"}>
                <FormControl>
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_NAME")}
                        type="text"
                        className={"width-100"}
                        name={"NAME"}
                        value={dbName}
                        autoComplete="off"
                        onChange={(e) => {
                            setDbName(e.target.value);
                            debounceChangeHandler('NAME', e.target.value);
                        }}
                    />
                </FormControl>
                <div className="flex width-100">
                    <Autocomplete
                        disablePortal
                        options={connectionList}
                        value={connection || '-- Select --'}
                        size="small"
                        sx={{ flexGrow: 1, minWidth: "140px" }}
                        getOptionLabel={(option) => option}
                        onChange={(e, value) => {
                            debounceChangeHandler('PREDEFINED', value);
                            setConnection(value);
                        }}
                        renderInput={(params) => <TextField
                            {...params}
                            label={t("LBL_DATA_CONN")}
                            onChange={(e) => {
                                debounceChangeHandler('PREDEFINED', e.target.value);
                                setConnection(e.target.value);
                            }}
                            variant="outlined"
                            size="small"
                            name={"DATA_CONN"}
                            autoComplete="off"
                        />
                        }
                    />
                    <LightTooltip title={t('LBL_PDC_BTN')}>
                        <Button
                            variant="outlined"
                            size="small"
                            disableElevation
                            className="margin-left-4"
                            sx={{
                                width: "40px",
                                padding: "4px 8px",
                                minWidth: "40px",
                            }}
                            onClick={() => {
                                setPdcModal(5);
                            }}>
                            <span className="FLOWSHEET"></span>
                        </Button>
                    </LightTooltip>
                    <Button
                        sx={{ minWidth: "175px", width: "175px", height: "34px" }}
                        variant="outlined"
                        size="small"
                        onClick={() => connectToDb(connection)}
                        className="margin-left-4"
                        disabled={connection === '' || connection === '-- Select --' || connection === null}
                    >
                        {btnLoading ? <CircularProgress size={"16px"} />: `${t('BTN_EXECUTE_SQL')}(${t('LBL_PREVIEW')})`}
                    </Button>
                </div>
                <div className='flex'>
                    <LabeledCheckbox
                        sx={{ minWidth: "140px" }}
                        label={t("BTN_IS_CACHEABLE")}
                        handleCheck={(checked) => {
                            setCacheResult(checked);
                            debounceChangeHandler('IS_CACHEABLE', stringifyJSONAttributes(checked));
                        }}
                        checked={cacheResult}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        className="bttn-database-navigator"
                        onClick={() => setOpenDrawer(true)}
                        disabled={connection === '' || connection === '-- Select --'}
                        style={{ minWidth: "170px", flex: "1" }}
                    >
                        <span style={{ opacity: connection === '' || connection === '-- Select --' ? '0.6' : '1' }} className="DATABASE"></span>
                        <span > {t("DIAG_DATABASE_VIEWER_TIT")} </span>
                    </Button>
                </div>

                <FormControl
                    className="width-100 query-textarea"
                    size="small"
                    sx={{ marginTop: "1rem" }}
                >
                    <TextField
                        multiline={true}
                        rows={5}
                        inputProps={{ style: { whiteSpace: "pre-wrap", overflowWrap: "break-word", overflowY: 'auto', wrap: "soft" } }}
                        className=""
                        size="medium"
                        type="text"
                        value={sqlQuery}
                        placeholder={t("TXT_ENTER_SQL_QUERY_HERE")}
                        onChange={(e) => {
                            const value = e.target.value;
                            debounceChangeHandler('VALUE', value);
                            setSqlQuery(value);
                        }}
                    />
                </FormControl>
                <Box sx={{ height: "100%", border: 1, borderColor: "divider", borderRadius: "5px" }}>
                    <Box >
                        <Tabs
                            sx={{ backgroundColor: "#fff", borderTopRightRadius: "5px", borderTopLeftRadius: "5px", borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}
                            value={currTab}
                            onChange={handleTabChange}
                        >
                            <Tab
                                label={
                                    <div className="flex align-center">
                                        {t('LBL_DYNAMIC_WHERE_CLAUSE_VALUES')}
                                    </div>
                                }
                            />
                            <Tab
                                label={
                                    <div className="flex align-center">
                                        {t('LBL_COLUMNS_RETURN_VALUES')}
                                    </div>
                                }
                            />
                        </Tabs>
                    </Box>
                    <Box className="height-100-tab width-100">
                        <TabPanel className="height-100 width-100" value={currTab} index={0}>
                            <DynamicClauseValues dynamicClauses={inArgs} currTab={currTab} />
                        </TabPanel>
                        <TabPanel className="height-100 width-100" value={currTab} index={1}>
                            <ColumnReturnValues columnReturnValues={outArgs} currTab={currTab} />
                        </TabPanel>
                    </Box>
                </Box>
            </Stack>
            <Repository 
                option={pdcModal} 
                setOption={setPdcModal} 
                handleClose={() => setPdcModal(null)} 
                dir='OUT' 
                pdcName={connection}
                onPdcSelect={(selectedPdcData) => {                    // Update connection with selected PDC
                    if (selectedPdcData.connectionName) {
                        setConnection(selectedPdcData.connectionName);
                        debounceChangeHandler('PREDEFINED', selectedPdcData.connectionName);
                    }
                }}
            />
            <SQLQueryPreview
                open={sqlQueryPreview}
                close={() => setSqlQueryPreview(false)}
                connection={connection}
                queryAccount={queryAccount || account}
                schema={schema}
                sqlQueryToRun={sqlQuery}
            />
            <DatabaseNavigator
                setConnectionLoading={handleLoading}
                showAlert={setShowAlert}
                alreadyConnected={false}
                fromCustomDB={true}
                selectedConnection={connection}
                selectedSchema={schema}
                updateSchema={updateSchema}
                updateConnection={updateConnection}
                open={openDrawer}
                handleClose={handleClose}
                sqlQueryToRun={sqlQuery}
                handleQuery={handleSqlQueryToRun}
                type='CUSTOM_DB'
                setOpenDrawer={() => setOpenDrawer(false)}
            />
            <Snackbar
                open={showAlert.isOpen}
                autoHideDuration={showAlert.type !== "success" ? null : 6000}
                onClose={() => handleAlertClose()}>
                <Alert
                    onClose={() => handleAlertClose()}
                    severity={showAlert.type}
                    variant="filled"
                    sx={{ maxWidth: '500px' }}>
                    {showAlert.mssg}
                </Alert>
            </Snackbar>
            <SchemaModal
                schema={schema}
                setSchema={setSchema}
                openModal={schemaModal}
                handleClose={() => {
                    setSchemaModal(false)
                }}
                okHandler={(val) => {
                    debounceChangeHandler("SCHEMA", val);
                    tablesHandler(val, connection);
                    setSchemaModal(false);
                    setSqlQueryPreview(true);
                    dispatch(treeActions.setConnectedPDC({
                        data: {
                            CONNECTION: connection,
                            SCHEMA: val
                        }
                    }))
                }}
                connection={connection}
            />
        </Box>
    )
}

export default CustomDBProperties;